import { redirect } from '@tanstack/react-router';
import { createMiddleware } from '@tanstack/react-start';
import z from 'zod';

import { sessionMiddleware } from './session';

import type { TLAIUserInfo } from '@/types/api';

const TLAIUserInfoSchema = z.object({
  name: z.string(),
  preferred_username: z.string(),
  given_name: z.string(),
  family_name: z.string(),
  email: z.string(),
  realm_access: z
    .union([
      z.object({
        roles: z.array(z.string()),
      }),
      z.array(z.string()),
    ])
    .optional(),
}) satisfies z.ZodType<TLAIUserInfo>;

export const userInfoMiddleware = createMiddleware({ type: 'function' })
  .middleware([sessionMiddleware])
  .inputValidator(z.object({ redirectTo: z.string() }).passthrough().optional())
  .server(async ({ next, context: { session }, data }) => {
    if (!session.authentication || !session.authentication.accessToken) {
      console.info('[USER] No access token found. User is not authenticated.');
      throw redirect({ to: '/login', search: { redirectTo: data?.redirectTo } });
    }

    const accessToken = session.authentication.accessToken;

    try {
      console.info('[USER] Access token is present, parsing token.');
      const base64Url = accessToken.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const tokenPayload = Buffer.from(base64, 'base64').toString('utf-8');
      const userInfo = JSON.parse(tokenPayload);

      const { data: userInfoData, error } = await TLAIUserInfoSchema.safeParseAsync(userInfo);

      if (error) {
        console.error('[USER] Recieved malformed user info:', error);
        throw redirect({ to: '/login', search: { redirectTo: data?.redirectTo } });
      }

      return next({ context: { user: userInfoData } });
    } catch (error) {
      console.error('[USER] Error parsing token:', error);
      throw redirect({ to: '/login', search: { redirectTo: data?.redirectTo } });
    }
  });
